import { FileIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { getFileExtension } from '@components/FileViewer/fileHelpers'
import { isInlineImageMime } from '@lib/mime'
import { GetFileType } from '@orbit/api-types'
import React, { useEffect, useRef } from 'react'
import { RemoveButton, ThumbnailDocIcon, ThumbnailGrid, ThumbnailItem } from './AttachmentSection.styles'

interface PendingThumbnailsProps {
	files: File[]
	onRemove: (index: number) => void
	/** Files the entity already has, shown first — an edit that takes one off deletes it on save. */
	savedFiles?: GetFileType[]
	onRemoveSaved?: (fileId: string) => void
}

/**
 * Files picked but not uploaded yet, as the attachment grid shows them: a
 * browser-drawable image's preview, or anything else's extension (a HEIC
 * photo included), each with its ✕ — after the entity's saved files, when an
 * edit hands those in. The grid on its own, so a picker can put its own way to
 * add more around it.
 */
const PendingThumbnails: React.FC<PendingThumbnailsProps> = ({ files, onRemove, savedFiles = [], onRemoveSaved }) => {
	const previewUrls = useRef<Map<File, string>>(new Map())

	// A preview outlives neither its file's removal nor the grid.
	useEffect(() => {
		const urls = previewUrls.current
		return () => {
			urls.forEach(url => URL.revokeObjectURL(url))
			urls.clear()
		}
	}, [])

	const previewUrl = (file: File): string => {
		if (!previewUrls.current.has(file)) previewUrls.current.set(file, URL.createObjectURL(file))
		return previewUrls.current.get(file)!
	}

	const remove = (index: number) => {
		const url = previewUrls.current.get(files[index])
		if (url) {
			URL.revokeObjectURL(url)
			previewUrls.current.delete(files[index])
		}
		onRemove(index)
	}

	return (
		<ThumbnailGrid>
			{savedFiles.map(file => (
				<ThumbnailItem key={file.id}>
					{isInlineImageMime(file.mimeType) ? (
						<img src={file.url} alt={file.fileName} />
					) : (
						<ThumbnailDocIcon>
							<FileIcon sx={{ fontSize: iconSizes.lg }} />
							<span>{getFileExtension(file.fileName)}</span>
						</ThumbnailDocIcon>
					)}
					<RemoveButton
						type="button"
						onClick={() => onRemoveSaved?.(file.id)}
						title="Remove"
						aria-label={`Remove ${file.fileName}`}
					>
						✕
					</RemoveButton>
				</ThumbnailItem>
			))}
			{files.map((file, index) => (
				<ThumbnailItem key={`${file.name}-${index}`}>
					{isInlineImageMime(file.type) ? (
						<img src={previewUrl(file)} alt={file.name} />
					) : (
						<ThumbnailDocIcon>
							<FileIcon sx={{ fontSize: iconSizes.lg }} />
							<span>{getFileExtension(file.name)}</span>
						</ThumbnailDocIcon>
					)}
					<RemoveButton type="button" onClick={() => remove(index)} title="Remove" aria-label={`Remove ${file.name}`}>
						✕
					</RemoveButton>
				</ThumbnailItem>
			))}
		</ThumbnailGrid>
	)
}

export default PendingThumbnails
